import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { employmentTypeLabel, feerLevelLabel } from "@/lib/constants";
import PublicNav from "@/components/PublicNav";
import { MapPin, Briefcase, Bookmark, BookmarkCheck, Send, Laptop, Users } from "lucide-react";

export default function JobDetail() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [applied, setApplied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/offers/${offerId}`);
        setOffer(res.data);
        const sim = await api.get("/offers");
        setSimilar(sim.data.filter((o) => o.offer_id !== offerId).slice(0, 3));
      } catch {
        setNotFound(true);
      }
    };
    load();
  }, [offerId]);

  useEffect(() => {
    if (!user || user.role !== "candidat") return;
    const loadState = async () => {
      try {
        const [apps, savedRes] = await Promise.all([
          api.get("/applications/mine"),
          api.get("/saved/mine"),
        ]);
        setApplied(apps.data.some((a) => a.offer_id === offerId));
        setSaved(savedRes.data.some((o) => o.offer_id === offerId));
      } catch {
        // ignore
      }
    };
    loadState();
  }, [user, offerId]);

  const handleApply = async () => {
    setError("");
    setMessage("");
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      await api.post(`/offers/${offerId}/apply`);
      setApplied(true);
      setMessage("Votre candidature a été envoyée avec succès.");
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleSave = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      const res = await api.post(`/offers/${offerId}/save`);
      setSaved(res.data.saved);
    } catch {
      // ignore
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div data-testid="job-not-found" className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-500">
          Offre introuvable ou expirée.{" "}
          <Link to="/jobs" className="text-brand-turquoise font-medium hover:underline">
            Voir toutes les offres
          </Link>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div className="max-w-5xl mx-auto px-6 py-24 flex justify-center">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="job-detail-page" className="min-h-screen bg-slate-50">
      <PublicNav />

      <div className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-slate-200 p-8">
            <div className="flex items-start justify-between gap-4 mb-2">
              <h1 data-testid="job-title" className="text-2xl font-bold text-brand-navy">{offer.title}</h1>
              {offer.remote && (
                <span className="flex items-center gap-1 text-xs font-medium text-brand-blue bg-brand-blue/10 rounded-full px-3 py-1.5 shrink-0">
                  <Laptop className="w-3.5 h-3.5" /> Remote
                </span>
              )}
            </div>
            <p className="text-slate-600 font-medium flex items-center gap-1.5 mb-1">
              <Briefcase className="w-4 h-4 text-slate-400" /> {offer.company}
            </p>
            {offer.company_profile_id && (
              <Link
                data-testid="job-company-link"
                to={`/entreprises/${offer.company_profile_id}`}
                className="text-sm text-brand-turquoise font-medium hover:underline inline-block mb-1"
              >
                Voir le profil de l'entreprise
              </Link>
            )}
            <p className="text-slate-500 text-sm flex items-center gap-1.5 mb-6">
              <MapPin className="w-4 h-4 text-slate-400" />
              {[offer.city, offer.country].filter(Boolean).join(", ")}
              {offer.positions > 1 && (
                <span className="flex items-center gap-1 ml-3">
                  <Users className="w-4 h-4 text-slate-400" /> {offer.positions} postes
                </span>
              )}
            </p>

            <div className="flex flex-wrap gap-2 mb-8">
              <span className="text-xs bg-brand-sky text-brand-navy rounded-full px-3 py-1.5 font-medium">
                {employmentTypeLabel(offer.employment_type)}
              </span>
              {offer.salary && (
                <span className="text-xs bg-brand-turquoise/10 text-brand-turquoise rounded-full px-3 py-1.5 font-medium">
                  {offer.salary} {offer.salary_currency || ""}
                </span>
              )}
              {offer.experience && (
                <span className="text-xs bg-slate-100 text-slate-600 rounded-full px-3 py-1.5">
                  Expérience : {offer.experience}
                </span>
              )}
              {offer.education && (
                <span className="text-xs bg-slate-100 text-slate-600 rounded-full px-3 py-1.5">
                  Formation : {offer.education}
                </span>
              )}
              {offer.noc_code && (
                <span data-testid="job-noc-chip" className="text-xs bg-brand-navy/5 text-brand-navy rounded-full px-3 py-1.5">
                  CNP/NOC : {offer.noc_code}
                </span>
              )}
              {offer.feer_level != null && offer.feer_level !== "" && (
                <span data-testid="job-feer-chip" className="text-xs bg-brand-navy/5 text-brand-navy rounded-full px-3 py-1.5">
                  {feerLevelLabel(offer.feer_level)}
                </span>
              )}
            </div>

            <h2 className="font-semibold text-brand-navy mb-3">Description du poste</h2>
            <p data-testid="job-description" className="text-slate-600 text-sm leading-relaxed whitespace-pre-line mb-8">
              {offer.description}
            </p>

            {offer.skills?.length > 0 && (
              <>
                <h2 className="font-semibold text-brand-navy mb-3">Compétences recherchées</h2>
                <div className="flex flex-wrap gap-2 mb-8">
                  {offer.skills.map((s) => (
                    <span key={s} className="text-xs bg-brand-sky text-brand-navy rounded-full px-3 py-1.5">{s}</span>
                  ))}
                </div>
              </>
            )}
            {offer.languages?.length > 0 && (
              <>
                <h2 className="font-semibold text-brand-navy mb-3">Langues</h2>
                <div className="flex flex-wrap gap-2">
                  {offer.languages.map((l) => (
                    <span key={l} className="text-xs bg-slate-100 text-slate-600 rounded-full px-3 py-1.5">{l}</span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div>
          <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-24">
            {message && (
              <div data-testid="apply-success" className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">{message}</div>
            )}
            {error && (
              <div data-testid="apply-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
            )}
            <button
              data-testid="apply-button"
              onClick={handleApply}
              disabled={applied}
              className="w-full flex items-center justify-center gap-2 bg-brand-turquoise text-white font-semibold rounded-lg px-5 py-3.5 hover:bg-brand-navy transition-colors duration-200 disabled:opacity-60 disabled:hover:bg-brand-turquoise mb-3"
            >
              <Send className="w-4 h-4" />
              {applied ? "Candidature envoyée" : "Postuler"}
            </button>
            <button
              data-testid="save-button"
              onClick={handleSave}
              className="w-full flex items-center justify-center gap-2 border border-slate-300 text-brand-navy font-medium rounded-lg px-5 py-3 hover:bg-slate-50 transition-colors duration-200"
            >
              {saved ? <BookmarkCheck className="w-4 h-4 text-brand-turquoise" /> : <Bookmark className="w-4 h-4" />}
              {saved ? "Sauvegardée" : "Sauvegarder"}
            </button>
            <p className="text-xs text-slate-400 mt-4 text-center">
              Publiée le {offer.created_at ? new Date(offer.created_at).toLocaleDateString("fr-CA") : "—"}
            </p>
            {offer.expires_at && (
              <p data-testid="job-expiry" className="text-xs text-slate-400 mt-1 text-center">
                Expire le {new Date(offer.expires_at).toLocaleDateString("fr-CA")}
              </p>
            )}
          </div>
        </div>
      </div>

      {similar.length > 0 && (
        <div data-testid="similar-offers" className="max-w-5xl mx-auto px-6 pb-16">
          <h2 className="font-bold text-brand-navy text-lg mb-4">Offres similaires</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {similar.map((o) => (
              <Link
                key={o.offer_id}
                to={`/jobs/${o.offer_id}`}
                data-testid={`similar-job-${o.offer_id}`}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:border-brand-turquoise hover:shadow-md transition-all duration-200"
              >
                <p className="font-semibold text-brand-navy mb-1">{o.title}</p>
                <p className="text-sm text-slate-500">{o.company}</p>
                <p className="text-xs text-slate-400 mt-1">{[o.city, o.country].filter(Boolean).join(", ")}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
